import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Award, RotateCcw } from 'lucide-react';
import { topicsApi, Topic, Question } from '../api/topics';
import { Button } from '../components/ui/Button';

export const QuizPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [topic, setTopic] = useState<Topic | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const fetchTopic = async () => {
      if (!slug) return;
      
      try {
        const data = await topicsApi.getBySlug(slug);
        setTopic(data);
      } catch (error) {
        console.error('Error fetching topic:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopic();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!topic || !topic.questions || topic.questions.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Geen vragen beschikbaar</h1>
          <p className="text-gray-600 mb-6">
            Er zijn momenteel geen vragen beschikbaar voor dit deel.
          </p>
          <Link to={`/topics/${slug}`}>
            <Button>Terug naar deel</Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentQuestion = topic.questions[currentQuestionIndex];
  const totalQuestions = topic.questions.length;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  
  // Convert question choices to options array
  const currentOptions = currentQuestion ? [
    { label: 'A', text: currentQuestion.choice_a },
    { label: 'B', text: currentQuestion.choice_b },
    { label: 'C', text: currentQuestion.choice_c },
    { label: 'D', text: currentQuestion.choice_d },
  ] : [];

  const handleAnswerSelect = (answer: string) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestionIndex]: answer
    });
  };

  const handleNext = () => {
    if (isLastQuestion) {
      setShowResults(true);
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentQuestionIndex(currentQuestionIndex - 1);
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setShowResults(false);
  };

  const calculateScore = () => {
    let correct = 0;
    topic.questions?.forEach((question, index) => {
      if (selectedAnswers[index] === question.answer) {
        correct++;
      }
    });
    return {
      correct,
      total: totalQuestions,
      percentage: Math.round((correct / totalQuestions) * 100)
    };
  };

  if (showResults) {
    const score = calculateScore();
    const isPassed = score.percentage >= 70;

    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link 
          to={`/topics/${slug}`}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Terug naar deel
        </Link>

        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
            isPassed ? 'bg-green-100' : 'bg-orange-100'
          }`}>
            <Award className={`w-10 h-10 ${isPassed ? 'text-green-600' : 'text-orange-600'}`} />
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">Kennistoets voltooid!</h1>
          <p className="text-xl text-gray-600 mb-8">
            Je hebt {score.correct} van de {score.total} vragen goed beantwoord
          </p>

          <div className="mb-8">
            <div className="text-5xl font-bold mb-2" style={{ color: isPassed ? '#16a34a' : '#ea580c' }}>
              {score.percentage}%
            </div>
            <p className={`text-lg font-medium ${isPassed ? 'text-green-600' : 'text-orange-600'}`}>
              {isPassed ? 'Goed gedaan!' : 'Blijf oefenen!'}
            </p>
          </div>

          <div className="space-y-4 max-w-2xl mx-auto mb-8">
            {topic.questions?.map((question, index) => {
              const userAnswer = selectedAnswers[index];
              const isCorrect = userAnswer === question.answer;
              const correctAnswerText = question[`choice_${question.answer.toLowerCase()}` as keyof Question] as string;
              
              return (
                <div key={question.id} className={`p-4 rounded-lg border-2 ${
                  isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                }`}>
                  <div className="flex items-start space-x-3">
                    {isCorrect ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900 mb-2">Vraag {index + 1}: {question.question}</p>
                      {!isCorrect && (
                        <div className="text-sm">
                          <p className="text-red-700 mb-1">
                            <span className="font-medium">Jouw antwoord:</span> {userAnswer}
                          </p>
                          <p className="text-green-700">
                            <span className="font-medium">Correct antwoord:</span> {question.answer} - {correctAnswerText}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={handleRestart}
              className="inline-flex items-center px-6 py-3 rounded-md font-medium bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors border border-purple-200"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Opnieuw proberen
            </button>
            <Link to={`/topics/${slug}`}>
              <Button>Terug naar deel</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link 
        to={`/topics/${slug}`}
        className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Terug naar deel
      </Link>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
          <span>Kennistoets: {topic.title}</span>
          <span className="font-medium">Vraag {currentQuestionIndex + 1} van {totalQuestions}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="bg-white border border-gray-200 rounded-lg p-8 mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          {currentQuestion.question}
        </h2>

        <div className="space-y-3">
          {currentOptions.map((option) => (
            <button
              key={option.label}
              onClick={() => handleAnswerSelect(option.label)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedAnswers[currentQuestionIndex] === option.label
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center font-semibold ${
                  selectedAnswers[currentQuestionIndex] === option.label
                    ? 'border-blue-500 bg-blue-500 text-white'
                    : 'border-gray-300 text-gray-600'
                }`}>
                  {option.label}
                </div>
                <span className="text-gray-900">{option.text}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className={`inline-flex items-center px-6 py-3 rounded-md font-medium transition-colors ${
            currentQuestionIndex === 0
              ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
              : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
          }`}
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Vorige
        </button>

        <button
          onClick={handleNext}
          disabled={!selectedAnswers[currentQuestionIndex]}
          className={`inline-flex items-center px-6 py-3 rounded-md font-medium transition-colors ${
            !selectedAnswers[currentQuestionIndex]
              ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
              : isLastQuestion
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isLastQuestion ? 'Resultaten bekijken' : 'Volgende'}
          {!isLastQuestion && <ArrowLeft className="w-5 h-5 ml-2 rotate-180" />}
        </button>
      </div>
    </div>
  );
};

